import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginWithPassword } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginWithPassword(email, password);
      toast.success("Welcome back");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: form */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-12">
            <div className="h-7 w-7 bg-primary flex items-center justify-center" style={{ borderRadius: 2 }}>
              <Sparkles className="h-4 w-4 text-primary-foreground" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-serif text-lg leading-none" style={{ fontWeight: 500 }}>GT Engine</div>
              <div className="overline mt-1">CFO Appointments</div>
            </div>
          </div>

          <div className="overline mb-3">Sign in</div>
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tight mb-2" style={{ fontWeight: 350 }}>
            Welcome back.
          </h1>
          <p className="text-muted-foreground mb-10 max-w-xs">
            Your command center for booking 10+ CFO meetings every week.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="overline">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="mt-2 input-crisp"
                placeholder="you@company.com.au"
              />
            </div>
            <div>
              <Label htmlFor="password" className="overline">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="mt-2 input-crisp"
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full input-crisp"
              style={{ borderRadius: 2 }}
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px bg-border flex-1" />
            <div className="text-xs text-muted-foreground uppercase tracking-widest">Or</div>
            <div className="h-px bg-border flex-1" />
          </div>

          <Button
            variant="outline"
            onClick={handleGoogle}
            data-testid="login-google"
            className="w-full input-crisp"
            style={{ borderRadius: 2 }}
          >
            Continue with Google
          </Button>

          <div className="mt-10 text-sm text-muted-foreground">
            New here?{" "}
            <Link to="/signup" className="text-foreground underline-offset-4 hover:underline" data-testid="link-signup">
              Create an account
            </Link>
          </div>
        </div>
      </div>

      {/* Right: image / editorial */}
      <div className="hidden lg:block relative auth-bg overflow-hidden border-l border-border">
        <img
          alt=""
          src="https://images.pexels.com/photos/20697911/pexels-photo-20697911.png?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1000"
          className="absolute inset-0 h-full w-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-foreground/30 mix-blend-multiply" />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <div className="max-w-md text-background">
            <div className="overline text-background/70 mb-4">Mandate</div>
            <p className="font-serif text-3xl leading-tight" style={{ fontWeight: 350 }}>
              From cold outreach to a CFO's calendar, in one engine.
            </p>
            <p className="mt-4 text-sm text-background/70">
              Multi-site Australian operators · Manufacturing · Logistics · Aged Care · Construction · Retail
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
